import {
  comboKey,
  DEFAULT_HOTKEY_SETTINGS,
  hasModifier,
  Hotkey,
  HotkeySettings,
  isReservedCombo,
} from '../hotkeys';

const STORAGE_KEY = 'casa:hotkeys';
const EXPORT_VERSION = 1;
const EXPORT_FILENAME = 'casa-shortcuts.json';

const isValidHotkey = (value: any): boolean =>
  !!value &&
  typeof value.code === 'string' &&
  typeof value.path === 'string' &&
  typeof value.label === 'string';

/**
 * Coerces an unknown array into clean, de-duplicated hotkeys. When `strict` is
 * set, drops combos that the dialog would reject (no modifier or reserved by
 * another feature) so imports cannot bypass the validation rules.
 */
export const sanitizeHotkeys = (list: any, strict = false): Hotkey[] => {
  if (!Array.isArray(list)) return [];
  const seen = new Set<string>();
  const result: Hotkey[] = [];
  for (const item of list) {
    if (!isValidHotkey(item)) continue;
    const hotkey: Hotkey = {
      code: item.code,
      alt: !!item.alt,
      ctrl: !!item.ctrl,
      shift: !!item.shift,
      path: item.path,
      label: item.label,
    };
    if (strict && (!hasModifier(hotkey) || isReservedCombo(hotkey))) continue;
    const key = comboKey(hotkey);
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(hotkey);
  }
  return result;
};

const normalizeSettings = (parsed: any): HotkeySettings => ({
  enabled:
    typeof parsed?.enabled === 'boolean'
      ? parsed.enabled
      : DEFAULT_HOTKEY_SETTINGS.enabled,
  showBadges:
    typeof parsed?.showBadges === 'boolean'
      ? parsed.showBadges
      : DEFAULT_HOTKEY_SETTINGS.showBadges,
  hotkeys: Array.isArray(parsed?.hotkeys)
    ? sanitizeHotkeys(parsed.hotkeys)
    : DEFAULT_HOTKEY_SETTINGS.hotkeys,
});

export const loadHotkeySettings = (): HotkeySettings => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_HOTKEY_SETTINGS;
    return normalizeSettings(JSON.parse(raw));
  } catch {
    return DEFAULT_HOTKEY_SETTINGS;
  }
};

export const saveHotkeySettings = (settings: HotkeySettings): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // ignore storage write failures (quota / private mode)
  }
};

export const STORAGE_KEY_NAME = STORAGE_KEY;

/** Downloads the current shortcuts as a shareable JSON file. */
export const exportHotkeySettings = (settings: HotkeySettings): void => {
  const payload = {
    version: EXPORT_VERSION,
    showBadges: settings.showBadges,
    hotkeys: settings.hotkeys,
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = EXPORT_FILENAME;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

export interface ImportedHotkeys {
  hotkeys: Hotkey[];
  showBadges?: boolean;
}

export class NoValidShortcutsError extends Error {}

/** Parses a previously exported JSON file. Throws on invalid input. */
export const parseImportedSettings = (json: string): ImportedHotkeys => {
  const parsed = JSON.parse(json);
  const hotkeys = sanitizeHotkeys(parsed?.hotkeys, true);
  if (!hotkeys.length) {
    throw new NoValidShortcutsError('No valid shortcuts found in the file.');
  }
  return {
    hotkeys,
    showBadges:
      typeof parsed?.showBadges === 'boolean' ? parsed.showBadges : undefined,
  };
};
